/** Ticker Talk tab. Headlines with transparent topic tags.
 *
 * Does not use the word "sentiment" for anything derived from keywords.
 * The tone column is an internal rule-based method and is labelled as
 * experimental everywhere it appears. No SentiSense.
 */
import { useMemo, useState } from "react";
import { StatusBadge } from "@/components/wheel/StatusBadge";
import {
  CachedBadge, DataNotice, EmptyNote, MockBadge, SectionTitle, WorkspacePanel,
} from "@/workspace/primitives";
import { TALK_TOPICS, talkFeed } from "@/workspace/mockTickerData";

const TONE = {
  constructive: { dir: "▲", color: "#10B981", label: "Constructive" },
  cautious: { dir: "▼", color: "#EF4444", label: "Cautious" },
  neutral: { dir: "●", color: "var(--hoip-text-secondary)", label: "Neutral" },
};

const CLUSTER_THEMES = {
  1: "Scheduled disclosure",
  2: "Positioning and flow",
  3: "Category demand",
  4: "Supply chain and regional demand",
  5: "Capital returns and coverage",
  6: "Programme timing",
};

function ToneCell({ tone }) {
  const t = TONE[tone] || TONE.neutral;
  return (
    <span className="font-mono-data text-[10px] whitespace-nowrap" style={{ color: t.color }}>
      {t.dir} {t.label}
    </span>
  );
}

export function TickerTalkTab({ t }) {
  const feed = t.live ? (t.talkItems || []) : talkFeed(t.ticker);
  const [topic, setTopic] = useState("All");
  const rows = useMemo(
    () => (topic === "All" ? feed : feed.filter((r) => (r.topics || []).includes(topic))),
    [feed, topic],
  );
  const clusters = useMemo(() => {
    const map = new Map();
    feed.forEach((r) => {
      if (r.cluster == null) return;
      const list = map.get(r.cluster) || [];
      list.push(r);
      map.set(r.cluster, list);
    });
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [feed]);
  const toneCounts = useMemo(() => {
    const c = { constructive: 0, cautious: 0, neutral: 0 };
    feed.forEach((r) => {
      if (TONE[r.tone]) c[r.tone] += 1;
    });
    return c;
  }, [feed]);

  if (t.live && (t.resourceStatus?.talk === "loading" || t.resourceStatus?.talk === "idle")) {
    return (
      <WorkspacePanel title="Ticker Talk" testid="talk-loading">
        <p className="text-[12px]" style={{ color: "var(--hoip-text-secondary)" }}>Loading cached headlines…</p>
      </WorkspacePanel>
    );
  }
  if (t.live && t.resourceStatus?.talk === "error") {
    return (
      <WorkspacePanel title="Ticker Talk" testid="talk-error">
        <p className="text-[12px]" style={{ color: "var(--hoip-text-secondary)" }}>
          {t.resourceStatus.talkError || "data unavailable"}
        </p>
      </WorkspacePanel>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5" data-testid="talk-topic-filter">
        {["All", ...TALK_TOPICS].map((x) => {
          const active = topic === x;
          return (
            <button
              key={x}
              type="button"
              onClick={() => setTopic(x)}
              className="text-[10px] px-2 py-0.5 rounded-sm border"
              style={{
                color: active ? "var(--hoip-bg)" : "var(--hoip-text-secondary)",
                background: active ? "var(--hoip-text-primary)" : "var(--hoip-card)",
                borderColor: active ? "var(--hoip-text-primary)" : "var(--hoip-border)",
              }}
            >
              {x}
            </button>
          );
        })}
      </div>

      <WorkspacePanel
        title="Headlines"
        subtitle={`${rows.length} of ${feed.length} items`}
        notice={t.live ? <CachedBadge /> : <MockBadge />}
        hint={t.live ? "Cached Ticker Talk headlines." : "Fixture headlines shaped like the existing house-feed items (kind, date, headline, summary)."}
        testid="talk-feed"
      >
        {rows.length ? (
          <div className="overflow-x-auto min-w-0">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left uppercase tracking-wide text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                  <th className="py-1 pr-3 font-medium">Date</th>
                  <th className="py-1 pr-3 font-medium">Headline</th>
                  <th className="py-1 pr-3 font-medium">Topics</th>
                  <th className="py-1 font-medium">Tone</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t align-top" style={{ borderColor: "var(--hoip-divider)" }}>
                    <td className="py-1.5 pr-3 font-mono-data text-[10px] whitespace-nowrap">{r.date}</td>
                    <td className="py-1.5 pr-3">
                      <div className="leading-snug">{r.headline}</div>
                      <div className="text-[9px] mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>{r.source}</div>
                    </td>
                    <td className="py-1.5 pr-3">
                      <div className="flex flex-wrap gap-1">
                        {(r.topics || []).map((x) => (
                          <span
                            key={x}
                            className="text-[9px] px-1 py-0.5 rounded-sm border whitespace-nowrap"
                            style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}
                          >
                            {x}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-1.5">{r.tone ? <ToneCell tone={r.tone} /> : <span style={{ color: "var(--hoip-text-tertiary)" }}>not evaluated</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyNote>No headlines carry that topic in this view.</EmptyNote>
        )}
      </WorkspacePanel>

      <WorkspacePanel
        title="Experimental rule-based tone"
        subtitle="MarkovLab internal method · mock mode"
        notice={<StatusBadge tier="caution" text="Experimental rule-based tone" testid="talk-experimental-badge" />}
        hint={t.live ? "Tone is not evaluated in this preview unless the cached item already carries it. No SentiSense." : "Placeholder for the internal headline-tone method. In mock mode the tone column is a fixture and no classifier runs."}
        testid="talk-tone"
      >
        <p className="text-[11px] leading-relaxed" style={{ color: "var(--hoip-text-secondary)" }}>
          Tone is an internal, transparent rule over the headline text. It is not a vendor score, and it is not called
          sentiment. Every classification will be traceable to the terms that produced it, so a reader can disagree with
          it on the evidence.
        </p>
        <div className="mt-2 pt-2 border-t" style={{ borderColor: "var(--hoip-divider)" }}>
          <SectionTitle>Distribution across this feed</SectionTitle>
          <div className="space-y-1">
            {Object.entries(toneCounts).map(([k, v]) => {
              const tone = TONE[k] || TONE.neutral;
              const pct = feed.length ? (v / feed.length) * 100 : 0;
              return (
                <div key={k} className="flex items-center gap-2 text-[10px]">
                  <span className="w-20 shrink-0" style={{ color: tone.color }}>{tone.dir} {tone.label}</span>
                  <div className="h-1.5 rounded-sm overflow-hidden flex-1" style={{ background: "var(--hoip-hover)" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: tone.color }} />
                  </div>
                  <span className="font-mono-data shrink-0 w-6 text-right">{v}</span>
                </div>
              );
            })}
          </div>
        </div>
      </WorkspacePanel>

      <WorkspacePanel
        title="Clustering"
        subtitle="Headlines grouped by shared theme"
        notice={t.live ? <CachedBadge /> : <MockBadge />}
        hint={t.live ? "Clustering is not evaluated from current cached headlines." : "Clusters are fixtures. A later release would group on shared terms and entities, not on a vendor topic field."}
        testid="talk-clusters"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
          {t.live && !clusters.length ? (
            <p className="text-[12px] py-1.5" style={{ color: "var(--hoip-text-secondary)" }}>
              Not available from current cached data
            </p>
          ) : null}
          {clusters.map(([id, items]) => (
            <div key={id} className="py-1.5 border-t" style={{ borderColor: "var(--hoip-divider)" }}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[11px]">{CLUSTER_THEMES[id] || `Cluster ${id}`}</span>
                <span className="text-[9px] font-mono-data shrink-0" style={{ color: "var(--hoip-text-tertiary)" }}>
                  {items.length} item{items.length === 1 ? "" : "s"}
                </span>
              </div>
              <ul className="mt-0.5 space-y-0.5">
                {items.map((i) => (
                  <li key={i.id} className="text-[9px] leading-snug truncate" style={{ color: "var(--hoip-text-tertiary)" }}>
                    {i.date} · {i.headline}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-2 pt-2 border-t" style={{ borderColor: "var(--hoip-divider)" }}>
          <DataNotice adminText="Headlines would come from the existing FinancialData.net house feed already cached in Mongo. Topic tags, tone and clusters are MarkovLab-derived and are not vendor fields." />
        </div>
      </WorkspacePanel>
    </div>
  );
}
